export * from './components/ToggleClipping';
export * from './components/ToggleClippingTooltip';
export * from './components/ToggleClippingEdit';
export * from './components/ToggleClippingEditTooltip';
export * from './state';

import App from '@frontend/application-container';
import { animate, hide_axes } from '@frontend/scene';
import { set_button_states } from '@frontend/non_graphical_ui_actions';
import { uid_calculate_global_bounding_box, uid_hide_rigid_move_manipulator } from '@frontend/ui_display';
import {
    DoubleSide,
    Geometry,
    Line,
    LineBasicMaterial,
    Mesh,
    MeshBasicMaterial,
    Plane,
    PlaneGeometry,
    Vector3,
} from 'three';
import { clipping_model_editable, clipping_on, clipping_planes } from './state';
import { get_from } from '@frontend/hashtable';
import { bind, maybe, pipe } from '@vaaas/fpts/option';
import type { Vertex } from '@phi/jcore';
import { restore_reference_transparency, set_temporary_reference_transparency } from '@features/references';

export function toggle_clipping(coming_from_clipping_editable_button = false) {
    // Hide rigid move manipulator, in case it was shown
    if (App.state.rigid_move_manipulator)
        uid_hide_rigid_move_manipulator(false, 0, true);

    // If we're currently in clipping_model_editable, exit this mode first. EXCEPT if this
    // call originates from the clipping editable button, in which case we just proceed.
    if (clipping_model_editable.value && !coming_from_clipping_editable_button)
        toggle_clipping_edit_model();

    if (!clipping_on.value) {
        initialize_and_show_clipping_planes();
        clipping_on.value = true;
        if (!coming_from_clipping_editable_button)
            clipping_model_editable.value = false;
        set_temporary_reference_transparency(false);
    } else {
        clipping_on.value = false;
        clipping_model_editable.value = false;
        destroy_and_hide_clipping_planes();
        restore_reference_transparency();
    }
    set_button_states();
    animate();
}

export function toggle_clipping_edit_model() {
    // Hide rigid move manipulator, in case it was shown
    if (App.state.rigid_move_manipulator)
        uid_hide_rigid_move_manipulator(false, 0, true);

    if (!clipping_model_editable.value) {
        clipping_model_editable.value = true;
        stop_raycasting_clipping_node();
        destroy_and_hide_clipping_planes(true); // Hide planes but continue clipping
        restore_reference_transparency();
    } else {
        clipping_model_editable.value = false;
        initialize_and_show_clipping_planes();
        start_raycasting_clipping_node();
        set_temporary_reference_transparency(false);
    }
    set_button_states();
    animate();
}

function initialize_and_show_clipping_planes() {
    // Hide rigid move manipulator, in case it was shown
    if (App.state.rigid_move_manipulator)
        uid_hide_rigid_move_manipulator(false, 0, true);

    // Calculate initial placement
    if (!uid_state.clipping_has_been_set) {
        uid_global_bb = uid_calculate_global_bounding_box(false, false, false);
        clipping_planes.xy.max = uid_global_bb.maxz + uid_global_bb.dimension / 10.0;
        clipping_planes.xy.min = uid_global_bb.minz - uid_global_bb.dimension / 10.0;
        clipping_planes.yz.max = uid_global_bb.maxx + uid_global_bb.dimension / 10.0;
        clipping_planes.yz.min = uid_global_bb.minx - uid_global_bb.dimension / 10.0;
        clipping_planes.xz.max = uid_global_bb.maxy + uid_global_bb.dimension / 10.0;
        clipping_planes.xz.min = uid_global_bb.miny - uid_global_bb.dimension / 10.0;
        uid_state.clipping_has_been_set = true;
    }

    show_clipping_planes();
    scene.add(uid_clipping_planes_raynode);
}

export function show_clipping_planes() {
    hide_axes();
    // Destroy node elements
    destroy_clipping_planes();

    // Declare meshes
    const xymax_mesh = new Mesh();
    const yzmax_mesh = new Mesh();
    const xzmax_mesh = new Mesh();
    const xymin_mesh = new Mesh();
    const yzmin_mesh = new Mesh();
    const xzmin_mesh = new Mesh();

    // Set mesh names
    xymax_mesh.name = 'xymax_clipping_plane';
    yzmax_mesh.name = 'yzmax_clipping_plane';
    xzmax_mesh.name = 'xzmax_clipping_plane';
    xymin_mesh.name = 'xymin_clipping_plane';
    yzmin_mesh.name = 'yzmin_clipping_plane';
    xzmin_mesh.name = 'xzmin_clipping_plane';

    // Set materials
    const clipping_plane_material = new MeshBasicMaterial({ color: App.state.colours.clipping.body });
    clipping_plane_material.side = DoubleSide;
    clipping_plane_material.transparent = true;
    clipping_plane_material.opacity = 0.1;
    clipping_plane_material.depthTest = true;
    clipping_plane_material.depthWrite = true;

    // Set geometries
    const dx = clipping_planes.yz.max - clipping_planes.yz.min;
    const dy = clipping_planes.xz.max - clipping_planes.xz.min;
    const dz = clipping_planes.xy.max - clipping_planes.xy.min;
    const cx = (clipping_planes.yz.max + clipping_planes.yz.min)/2.0;
    const cy = (clipping_planes.xz.max + clipping_planes.xz.min)/2.0;
    const cz = (clipping_planes.xy.max + clipping_planes.xy.min)/2.0;

    const xymax_geometry = new PlaneGeometry(dx, dy, 1);
    const yzmax_geometry = new PlaneGeometry(dz, dy, 1);
    const xzmax_geometry = new PlaneGeometry(dx, dz, 1);
    const xymin_geometry = new PlaneGeometry(dx, dy, 1);
    const yzmin_geometry = new PlaneGeometry(dz, dy, 1);
    const xzmin_geometry = new PlaneGeometry(dx, dz, 1);

    yzmax_geometry.rotateY(Math.PI / 2.0);
    xzmax_geometry.rotateX(Math.PI / 2.0);
    yzmin_geometry.rotateY(Math.PI / 2.0);
    xzmin_geometry.rotateX(Math.PI / 2.0);

    const xymax_shifted = clipping_planes.xy.max > 0 ? clipping_planes.xy.max * 1.001 : clipping_planes.xy.max / 1.001;
    const yzmax_shifted = clipping_planes.yz.max > 0 ? clipping_planes.yz.max * 1.001 : clipping_planes.yz.max / 1.001;
    const xzmax_shifted = clipping_planes.xz.max > 0 ? clipping_planes.xz.max * 1.001 : clipping_planes.xz.max / 1.001;
    const xymin_shifted = clipping_planes.xy.min < 0 ? clipping_planes.xy.min * 1.001 : clipping_planes.xy.min / 1.001;
    const yzmin_shifted = clipping_planes.yz.min < 0 ? clipping_planes.yz.min * 1.001 : clipping_planes.yz.min / 1.001;
    const xzmin_shifted = clipping_planes.xz.min < 0 ? clipping_planes.xz.min * 1.001 : clipping_planes.xz.min / 1.001;
    const xymax_shifted2 = clipping_planes.xy.max > 0 ? clipping_planes.xy.max * 1.002 : clipping_planes.xy.max / 1.002;
    const yzmax_shifted2 = clipping_planes.yz.max > 0 ? clipping_planes.yz.max * 1.002 : clipping_planes.yz.max / 1.002;
    const xzmax_shifted2 = clipping_planes.xz.max > 0 ? clipping_planes.xz.max * 1.002 : clipping_planes.xz.max / 1.002;
    const xymin_shifted2 = clipping_planes.xy.min < 0 ? clipping_planes.xy.min * 1.002 : clipping_planes.xy.min / 1.002;
    const yzmin_shifted2 = clipping_planes.yz.min < 0 ? clipping_planes.yz.min * 1.002 : clipping_planes.yz.min / 1.002;
    const xzmin_shifted2 = clipping_planes.xz.min < 0 ? clipping_planes.xz.min * 1.002 : clipping_planes.xz.min / 1.002;

    xymax_geometry.translate(cx, cy, xymax_shifted);
    yzmax_geometry.translate(yzmax_shifted, cy, cz);
    xzmax_geometry.translate(cx, xzmax_shifted, cz);
    xymin_geometry.translate(cx, cy, xymin_shifted);
    yzmin_geometry.translate(yzmin_shifted, cy, cz);
    xzmin_geometry.translate(cx, xzmin_shifted, cz);

    const localClippingPlane_xymax = new Plane(new Vector3(0, 0, -1), xymax_shifted2);
    const localClippingPlane_yzmax = new Plane(new Vector3(-1, 0, 0), yzmax_shifted2);
    const localClippingPlane_xzmax = new Plane(new Vector3(0, -1, 0), xzmax_shifted2);
    const localClippingPlane_xymin = new Plane(new Vector3(0, 0, 1), (-1) * xymin_shifted2);
    const localClippingPlane_yzmin = new Plane(new Vector3(1, 0, 0), (-1) * yzmin_shifted2);
    const localClippingPlane_xzmin = new Plane(new Vector3(0, 1, 0), (-1) * xzmin_shifted2);

    xymax_mesh.material = clipping_plane_material;
    yzmax_mesh.material = clipping_plane_material;
    xzmax_mesh.material = clipping_plane_material;
    xymin_mesh.material = clipping_plane_material;
    yzmin_mesh.material = clipping_plane_material;
    xzmin_mesh.material = clipping_plane_material;
    xymax_mesh.geometry = xymax_geometry;
    yzmax_mesh.geometry = yzmax_geometry;
    xzmax_mesh.geometry = xzmax_geometry;
    xymin_mesh.geometry = xymin_geometry;
    yzmin_mesh.geometry = yzmin_geometry;
    xzmin_mesh.geometry = xzmin_geometry;

    // Also add lines all around the planes
    const xymax_line = new Line();
    const xymin_line = new Line();
    const yzmax_line = new Line();
    const yzmin_line = new Line();
    const xzmax_line = new Line();
    const xzmin_line = new Line();

    xymax_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });
    xymin_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });
    yzmax_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });
    yzmin_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });
    xzmax_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });
    xzmin_line.material = new LineBasicMaterial({ color: App.state.colours.clipping.edges, linewidth: 2 });

    xymax_line.geometry = new Geometry();
    // @ts-ignore
    xymax_line.geometry.dynamic = true;
    xymax_line.geometry.vertices = [];
    xymax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));
    xymax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymax_shifted));
    xymax_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymax_shifted));
    xymax_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymax_shifted));
    xymax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));

    xymin_line.geometry = new Geometry();
    // @ts-ignore
    xymin_line.geometry.dynamic = true;
    xymin_line.geometry.vertices = [];
    xymin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymin_shifted));
    xymin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymin_shifted));
    xymin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymin_shifted));
    xymin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymin_shifted));
    xymin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymin_shifted));

    yzmax_line.geometry = new Geometry();
    // @ts-ignore
    yzmax_line.geometry.dynamic = true;
    yzmax_line.geometry.vertices = [];
    yzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));
    yzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymax_shifted));
    yzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymin_shifted));
    yzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymin_shifted));
    yzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));

    yzmin_line.geometry = new Geometry();
    // @ts-ignore
    yzmin_line.geometry.dynamic = true;
    yzmin_line.geometry.vertices = [];
    yzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymax_shifted));
    yzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymax_shifted));
    yzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymin_shifted));
    yzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymin_shifted));
    yzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymax_shifted));

    xzmax_line.geometry = new Geometry();
    // @ts-ignore
    xzmax_line.geometry.dynamic = true;
    xzmax_line.geometry.vertices = [];
    xzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));
    xzmax_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymax_shifted));
    xzmax_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmax_shifted, xymin_shifted));
    xzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymin_shifted));
    xzmax_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmax_shifted, xymax_shifted));

    xzmin_line.geometry = new Geometry();
    // @ts-ignore
    xzmin_line.geometry.dynamic = true;
    xzmin_line.geometry.vertices = [];
    xzmin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymax_shifted));
    xzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymax_shifted));
    xzmin_line.geometry.vertices.push(new Vector3(yzmin_shifted, xzmin_shifted, xymin_shifted));
    xzmin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymin_shifted));
    xzmin_line.geometry.vertices.push(new Vector3(yzmax_shifted, xzmin_shifted, xymax_shifted));

    // Add elements to node
    uid_clipping_planes_raynode.add(xymax_mesh);
    uid_clipping_planes_raynode.add(yzmax_mesh);
    uid_clipping_planes_raynode.add(xzmax_mesh);
    uid_clipping_planes_raynode.add(xymin_mesh);
    uid_clipping_planes_raynode.add(yzmin_mesh);
    uid_clipping_planes_raynode.add(xzmin_mesh);

    uid_clipping_planes_raynode.add(xymax_line);
    uid_clipping_planes_raynode.add(xymin_line);
    uid_clipping_planes_raynode.add(yzmax_line);
    uid_clipping_planes_raynode.add(yzmin_line);
    uid_clipping_planes_raynode.add(xzmax_line);
    uid_clipping_planes_raynode.add(xzmin_line);

    renderer.clippingPlanes = [
        localClippingPlane_xymax,
        localClippingPlane_yzmax,
        localClippingPlane_xzmax,
        localClippingPlane_xymin,
        localClippingPlane_yzmin,
        localClippingPlane_xzmin,
    ];
    renderer.localClippingEnabled = true;
    clipping_on.value = true;
    uid_raynode = uid_clipping_planes_raynode;
}

function stop_raycasting_clipping_node() {
    uid_state.clipping_plane_selected = '';
    uid_raynode = uid_objects_raynode;
}

function start_raycasting_clipping_node() {
    uid_state.clipping_plane_selected = '';
    uid_raynode = uid_clipping_planes_raynode;
}

export function destroy_and_hide_clipping_planes(continue_clipping = false) {
    hide_axes();
    destroy_clipping_planes();
    if (!continue_clipping) {
        renderer.clippingPlanes = [];
        renderer.localClippingEnabled = false;
    }
    clipping_on.value = false;
    uid_state.clipping_plane_selected = '';
    scene.remove(uid_clipping_planes_raynode);
    uid_raynode = uid_objects_raynode;
}

function destroy_clipping_planes() {
    for (let i = uid_clipping_planes_raynode.children.length - 1; i >= 0; i--) {
        const el = uid_clipping_planes_raynode.children[i];
        if (!el) continue;
        if (el instanceof Mesh) {
            if (el.geometry)
                el.geometry.dispose();
            if (el.material)
                el.material.dispose();
        }
        uid_clipping_planes_raynode.remove(el);
    }
}

export const is_vertex_clipped = (x: Vertex) => pipe(
    x,
    get_from(uid_vertices),
    bind((x) => x.object3d?.position),
    maybe(true, (x) => (
        x.x < clipping_planes.yz.min
        || x.x > clipping_planes.yz.max
        || x.y < clipping_planes.xz.min
        || x.y > clipping_planes.xz.max
        || x.z < clipping_planes.xy.min
        || x.z > clipping_planes.xy.max
    )),
);
